import { AlertColor, Chip, Tooltip } from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from "@fenetech/translations";
import { DocuSignEnvelopeStatusEnum, ThemeColorEnum } from "helpers/enums";
import { GetEnumAsListOfKeyValuePairs, isNullOrWhiteSpace } from "helpers/objects";
import { IESignatureDocument, IESignatureDocumentStatus } from "models/IESignature";
import Format from "helpers/fv.format";
import ESignatureAPI from "components/Setup/ESignature/ESignatureAPI";
import useWait from "helpers/context/Page/useWait";

interface IProps {
    eSignDocument: IESignatureDocument,
    launchESignatureConsentLogin: () => Promise<void>,
    refreshESignatureDocument: () => Promise<void>,
    setAlert: React.Dispatch<React.SetStateAction<[text: string, alertType: AlertColor] | null>>
}

const ESignatureChip: React.FC<IProps> = ({ eSignDocument, launchESignatureConsentLogin, refreshESignatureDocument, setAlert }: IProps) => {

    const tm = useTranslations();
    const wait = useWait();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        wait.Show(isLoading);
    }, [wait, isLoading]);

    const eSignatureStatusPhrase = useMemo(() => {
        const phrases: { key: string, value: number }[] = GetEnumAsListOfKeyValuePairs(Object.values(DocuSignEnvelopeStatusEnum));

        return phrases.find(p => p.value === eSignDocument.signatureStatus)?.key ?? "";
    }, [eSignDocument]);

    const chipColor: ThemeColorEnum = useMemo(() => {
        switch(eSignDocument.signatureStatus) {
            case DocuSignEnvelopeStatusEnum.Completed: case DocuSignEnvelopeStatusEnum.Signed:
                return ThemeColorEnum.Success;
            case DocuSignEnvelopeStatusEnum.Delivered: case DocuSignEnvelopeStatusEnum.Sent: case DocuSignEnvelopeStatusEnum.Correct:
            case DocuSignEnvelopeStatusEnum.Created: case DocuSignEnvelopeStatusEnum.Template:
                return ThemeColorEnum.Info;
            case DocuSignEnvelopeStatusEnum.Declined: case DocuSignEnvelopeStatusEnum.Deleted: case DocuSignEnvelopeStatusEnum.TimedOut: case DocuSignEnvelopeStatusEnum.Voided:
                return ThemeColorEnum.Error
            default:
                return ThemeColorEnum.Info;
        }
    }, [eSignDocument]);

    const onRefreshStatus = useCallback(async () => {
        setIsLoading(true);
        try {
            const eSignatureDocumentStatus: IESignatureDocumentStatus = {
                status: eSignDocument.signatureStatus,
                oKey: eSignDocument.oKey,
                documentReferenceID: eSignDocument.documentReferenceID
            }
            const refreshResult = await ESignatureAPI.RefreshDocumentStatus(eSignatureDocumentStatus);
            if (refreshResult.loginRequested) {
                await launchESignatureConsentLogin();
            } else if (!isNullOrWhiteSpace(refreshResult.message)) {
                setAlert([tm.Get(refreshResult.message), "error"]);
            } else {
                await refreshESignatureDocument();
            }
        }
        finally {
            setIsLoading(false);
        }
    }, [launchESignatureConsentLogin, eSignDocument, refreshESignatureDocument, setAlert, tm]);

    const tooltip = useMemo(() => {
        let tooltipText: string[] = [];

        switch(eSignDocument.signatureStatus) {
            case DocuSignEnvelopeStatusEnum.Delivered: case DocuSignEnvelopeStatusEnum.Sent: case DocuSignEnvelopeStatusEnum.Created:
                tooltipText.push(`${tm.Get("Email sent")} ${Format.FormatDate(eSignDocument.createDate)}`);
                tooltipText.push(`${tm.Get("Recipients:")} ${eSignDocument.signerEmail}`);
                break;
            default:
                tooltipText.push(`${tm.Get("Last Updated:")} ${Format.FormatDate(eSignDocument.lastUpdated)}`);
                tooltipText.push(`${tm.Get("Signer:")} ${eSignDocument.signerName}`);
        }

        return tooltipText.join("\n");
    }, [eSignDocument, tm]);

    const chipLabel = useMemo(() => {
        return <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>}>
            <div>{`${tm.Get("E-Signature")} ${tm.Get(eSignatureStatusPhrase)}`}</div>
        </Tooltip>
    }, [tooltip, eSignatureStatusPhrase, tm]);

    return <Chip
        label={chipLabel}
        color={chipColor}
        deleteIcon={<Tooltip title={tm.Get("Refresh")}><RefreshIcon /></Tooltip>}
        onDelete={onRefreshStatus}
    />
}

export default ESignatureChip;